import { Schema } from "effect";
import { utf8ByteLength } from "@papersflow/contracts/utf8";

/** Strict default for PapersFlow-owned research payloads at untrusted boundaries. */
export const ResearchBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

const ConfidenceSchema = Schema.Literals(["high", "medium", "low"]);

export const RESEARCH_BUNDLE_MAX_ENCODED_BYTES = 128 * 1_024;
export const RESEARCH_BASIS_MAX_ENCODED_BYTES = 128 * 1_024;
export const RESEARCH_REPORT_MAX_ENCODED_BYTES = 160 * 1_024;
/** Bounds the three validated canonical objects plus the browser-safe response envelope. */
export const AUTHORIZED_RESEARCH_REPORT_MAX_ENCODED_BYTES = 512 * 1_024;

function encodedJsonFits(value: unknown, maximumBytes: number): boolean | string {
  try {
    return (
      utf8ByteLength(JSON.stringify(value)) <= maximumBytes ||
      `encoded JSON must be at most ${maximumBytes} UTF-8 bytes`
    );
  } catch {
    return "must be JSON encodable";
  }
}

const boundedString = (maximumLength: number, minimumLength = 0) =>
  minimumLength > 0
    ? Schema.String.check(Schema.isMinLength(minimumLength), Schema.isMaxLength(maximumLength))
    : Schema.String.check(Schema.isMaxLength(maximumLength));
const BoundedIdentifierSchema = boundedString(180, 1);
const BoundedLabelSchema = boundedString(256);
const BoundedSummarySchema = boundedString(4_000);
const BoundedMarkdownSchema = boundedString(12_000);

const ResearchIdentifierSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(180),
);
const ResearchTitleSchema = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(512));
const ResearchQuestionSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(20_000),
);
const ResearchVersionSchema = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120));
const ResearchDomainSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(253),
  Schema.makeFilter(
    (value) =>
      /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/.test(value) || "must be a normalized lowercase domain",
  ),
);
const ResearchUrlSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(8_192),
  Schema.makeFilter((value) => /^https?:\/\//.test(value) || "must be an HTTP(S) URL"),
);
const ResearchPublicHttpUrlSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(4_096),
  Schema.makeFilter(
    (value) =>
      (canonicalizeResearchUrl(value, false) !== null &&
        /^https?:\/\/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?(?::\d{1,5})?(?:[/?#][^\s]*)?$/iu.test(
          value,
        )) ||
      "must be a public HTTP(S) URL without credentials",
  ),
);
const ResearchSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);
const ResearchCurrencySchema = Schema.String.check(
  Schema.makeFilter((value) => /^[A-Z]{3}$/.test(value) || "must be an ISO 4217 currency code"),
);
export const ResearchRunIdSchema = ResearchIdentifierSchema.pipe(Schema.brand("ResearchRunId"));
export const ResearchReportRevisionIdSchema = ResearchIdentifierSchema.pipe(
  Schema.brand("ResearchReportRevisionId"),
);

export const ResearchProductModeSchema = Schema.Literals([
  "quick",
  "deep",
  "expert",
  "exhaustive-2x",
  "exhaustive-4x",
  "exhaustive-8x",
]);

export const ResearchProcessorSchema = Schema.Literals([
  "core-fast",
  "pro-fast",
  "ultra-fast",
  "ultra2x-fast",
  "ultra4x-fast",
  "ultra8x-fast",
]);

export const ResearchApplicationStateSchema = Schema.Literals([
  "draft",
  "submitting",
  "queued",
  "researching",
  "finalizing",
  "rendering",
  "ready",
  "failed",
  "cancelled",
]);

export const ParallelRunStateSchema = Schema.Literals([
  "queued",
  "running",
  "completed",
  "failed",
  "cancelling",
  "cancelled",
  "action_required",
]);

export const ResearchProviderSubmissionStateSchema = Schema.Literals([
  "reserved",
  "claimed",
  "dispatching",
  "attached",
  "outcome-unknown",
  "not-accepted",
]);

export const ResearchCreditReservationStateSchema = Schema.Literals([
  "reserved",
  "needs-reconciliation",
  "settled",
  "released",
]);

export const ResearchSourcePolicySchema = Schema.Struct({
  mode: Schema.Literals([
    "open-web",
    "official-first",
    "trusted-domains-only",
    "exclude-selected-domains",
  ]),
  includeDomains: Schema.Array(ResearchDomainSchema).check(Schema.isMaxLength(32)),
  excludeDomains: Schema.Array(ResearchDomainSchema).check(Schema.isMaxLength(32)),
});

export const StartResearchResultSchema = Schema.Struct({
  researchRunId: ResearchRunIdSchema,
  reportArtifactId: ResearchIdentifierSchema,
  state: Schema.Literal("draft"),
});

const ResearchScopeSchema = Schema.Struct({
  question: ResearchQuestionSchema,
  timeframe: Schema.NullOr(boundedString(512)),
  geography: Schema.NullOr(boundedString(512)),
  assumptions: Schema.Array(boundedString(1_000)).check(Schema.isMaxLength(24)),
});

const ResearchFindingSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  title: boundedString(512),
  summary: BoundedSummarySchema,
  significance: boundedString(2_000),
  section_id: BoundedIdentifierSchema,
});

const ResearchSectionSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  title: boundedString(512),
  markdown: BoundedMarkdownSchema,
  finding_ids: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(64)),
  visual_ids: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(32)),
});

const ResearchMetricSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  label: BoundedLabelSchema,
  value: boundedString(512),
  unit: Schema.NullOr(boundedString(80)),
  context: boundedString(2_000),
});

const ResearchTableSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  title: boundedString(512),
  columns: Schema.Array(BoundedLabelSchema).check(Schema.isMaxLength(24)),
  rows: Schema.Array(Schema.Array(boundedString(1_000)).check(Schema.isMaxLength(24))).check(
    Schema.isMaxLength(200),
  ),
  notes: Schema.NullOr(BoundedSummarySchema),
});

const ResearchSeriesPointSchema = Schema.Struct({
  x: BoundedLabelSchema,
  y: Schema.Finite,
  series: BoundedIdentifierSchema,
});

const ResearchSeriesSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  title: boundedString(512),
  x_label: BoundedLabelSchema,
  y_label: BoundedLabelSchema,
  unit: Schema.NullOr(boundedString(80)),
  points: Schema.Array(ResearchSeriesPointSchema).check(Schema.isMaxLength(200)),
});

const ResearchVisualSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  type: Schema.Literals(["bar-chart", "line-chart", "flow", "timeline", "relationship", "matrix"]),
  title: boundedString(512),
  data_ref: BoundedIdentifierSchema,
  narrative: boundedString(2_000),
  orientation: Schema.NullOr(Schema.Literals(["horizontal", "vertical"])),
});

const ResearchGraphNodeSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  label: BoundedLabelSchema,
  kind: BoundedIdentifierSchema,
  summary: boundedString(2_000),
});

const ResearchGraphEdgeSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  source: BoundedIdentifierSchema,
  target: BoundedIdentifierSchema,
  relation: BoundedIdentifierSchema,
  label: BoundedLabelSchema,
});

const ResearchRecommendationSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  action: boundedString(2_000),
  rationale: BoundedSummarySchema,
  priority: Schema.Literals(["high", "medium", "low"]),
  finding_ids: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(64)),
});

const ResearchRiskSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  risk: boundedString(2_000),
  likelihood: Schema.Literals(["high", "medium", "low"]),
  impact: Schema.Literals(["high", "medium", "low"]),
  mitigation: BoundedSummarySchema,
});

const ResearchFollowUpQuestionSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  question: boundedString(2_000),
  reason: boundedString(2_000),
});

/** Canonical semantic result. Citations intentionally remain in the separate basis. */
export const ResearchBundleV1Schema = Schema.Struct({
  schema_version: Schema.Literal("research-bundle-v1"),
  title: ResearchTitleSchema,
  executive_summary: boundedString(12_000),
  scope: ResearchScopeSchema,
  methodology: BoundedSummarySchema,
  findings: Schema.Array(ResearchFindingSchema).check(Schema.isMaxLength(64)),
  sections: Schema.Array(ResearchSectionSchema).check(Schema.isMaxLength(32)),
  metrics: Schema.Array(ResearchMetricSchema).check(Schema.isMaxLength(64)),
  tables: Schema.Array(ResearchTableSchema).check(Schema.isMaxLength(16)),
  series: Schema.Array(ResearchSeriesSchema).check(Schema.isMaxLength(16)),
  visuals: Schema.Array(ResearchVisualSchema).check(Schema.isMaxLength(32)),
  graph_nodes: Schema.Array(ResearchGraphNodeSchema).check(Schema.isMaxLength(128)),
  graph_edges: Schema.Array(ResearchGraphEdgeSchema).check(Schema.isMaxLength(256)),
  recommendations: Schema.Array(ResearchRecommendationSchema).check(Schema.isMaxLength(32)),
  risks: Schema.Array(ResearchRiskSchema).check(Schema.isMaxLength(32)),
  limitations: Schema.Array(boundedString(2_000)).check(Schema.isMaxLength(32)),
  follow_up_questions: Schema.Array(ResearchFollowUpQuestionSchema).check(Schema.isMaxLength(32)),
}).check(Schema.makeFilter((value) => encodedJsonFits(value, RESEARCH_BUNDLE_MAX_ENCODED_BYTES)));

export const decodeResearchBundleV1 = Schema.decodeUnknownEffect(
  ResearchBundleV1Schema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchBundleV1Sync = Schema.decodeUnknownSync(
  ResearchBundleV1Schema,
  ResearchBoundaryParseOptions,
);
export const encodeResearchBundleV1 = Schema.encodeEffect(
  ResearchBundleV1Schema,
  ResearchBoundaryParseOptions,
);

export const ResearchSourceSchema = Schema.Struct({
  sourceId: ResearchIdentifierSchema,
  canonicalUrl: ResearchUrlSchema,
  displayUrl: ResearchUrlSchema,
  domain: ResearchDomainSchema,
  title: Schema.NullOr(Schema.String.check(Schema.isMaxLength(512))),
  referencedBy: Schema.Array(ResearchIdentifierSchema).check(Schema.isMaxLength(256)),
});

export const ResearchCitationLocatorSchema = Schema.Struct({
  kind: Schema.Literal("url-fragment"),
  targetUrl: ResearchPublicHttpUrlSchema.check(
    Schema.makeFilter((value) => /#\S+$/u.test(value) || "must contain a non-empty URL fragment"),
  ),
});

export const ResearchIndexedCitationSchema = Schema.Struct({
  sourceId: BoundedIdentifierSchema,
  excerpts: Schema.Array(boundedString(2_000)).check(Schema.isMaxLength(8)),
  locator: Schema.optionalKey(ResearchCitationLocatorSchema),
});

export const ResearchBasisIndexEntrySchema = Schema.Struct({
  fieldPath: boundedString(512),
  confidence: Schema.NullOr(ConfidenceSchema),
  methodNote: BoundedSummarySchema,
  citations: Schema.Array(ResearchIndexedCitationSchema).check(Schema.isMaxLength(32)),
});

function canonicalizeResearchUrl(value: string, removeFragment: boolean): string | null {
  try {
    const canonicalUrl = Schema.encodeSync(Schema.URLFromString)(
      Schema.decodeSync(Schema.URLFromString)(value),
    );
    if (!removeFragment) return canonicalUrl;
    const fragmentIndex = canonicalUrl.indexOf("#");
    return fragmentIndex === -1 ? canonicalUrl : canonicalUrl.slice(0, fragmentIndex);
  } catch {
    return null;
  }
}

function validateResearchBasisCitations(value: {
  readonly entries: ReadonlyArray<{
    readonly citations: ReadonlyArray<{
      readonly sourceId: string;
      readonly locator?: { readonly targetUrl: string };
    }>;
  }>;
  readonly sources: ReadonlyArray<{ readonly sourceId: string; readonly canonicalUrl: string }>;
}): boolean | string {
  for (const entry of value.entries) {
    for (const citation of entry.citations) {
      const source = value.sources.find(({ sourceId }) => sourceId === citation.sourceId);
      if (!source) return "every citation sourceId must resolve to a basis source";
      if (
        citation.locator &&
        canonicalizeResearchUrl(citation.locator.targetUrl, true) !==
          canonicalizeResearchUrl(source.canonicalUrl, false)
      ) {
        return "citation locator must target its source canonicalUrl";
      }
    }
  }
  return true;
}

export const ResearchBasisIndexSchema = Schema.Struct({
  entries: Schema.Array(ResearchBasisIndexEntrySchema).check(Schema.isMaxLength(512)),
  sources: Schema.Array(ResearchSourceSchema).check(Schema.isMaxLength(200)),
}).check(
  Schema.makeFilter(validateResearchBasisCitations),
  Schema.makeFilter((value) => encodedJsonFits(value, RESEARCH_BASIS_MAX_ENCODED_BYTES)),
);

export const ResearchDiagramModelSchema = Schema.Struct({
  id: BoundedIdentifierSchema,
  type: Schema.Literals(["flow", "timeline", "sequence", "relationship"]),
  title: boundedString(512),
  direction: Schema.Literals(["TB", "LR"]),
  nodes: Schema.Array(
    Schema.Struct({
      id: BoundedIdentifierSchema,
      label: BoundedLabelSchema,
      kind: BoundedIdentifierSchema,
    }),
  ).check(Schema.isMaxLength(128)),
  edges: Schema.Array(
    Schema.Struct({
      from: BoundedIdentifierSchema,
      to: BoundedIdentifierSchema,
      label: Schema.NullOr(BoundedLabelSchema),
    }),
  ).check(Schema.isMaxLength(256)),
});

const ResearchEvidencePropsSchema = {
  confidence: Schema.NullOr(ConfidenceSchema),
  sourceIds: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(64)),
};

const ReportRootElementSchema = Schema.Struct({
  type: Schema.Literal("ResearchReport"),
  props: Schema.Struct({ runId: ResearchRunIdSchema }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(512)),
});

const ReportHeaderElementSchema = Schema.Struct({
  type: Schema.Literal("ReportHeader"),
  props: Schema.Struct({
    title: boundedString(512),
    status: Schema.Literals(["researching", "ready"]),
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(32)),
});

const ExecutiveSummaryElementSchema = Schema.Struct({
  type: Schema.Literal("ExecutiveSummary"),
  props: Schema.Struct({
    content: boundedString(12_000),
    ...ResearchEvidencePropsSchema,
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(32)),
});

const FindingGridElementSchema = Schema.Struct({
  type: Schema.Literal("FindingGrid"),
  props: Schema.Struct({}),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(64)),
});

const FindingCardElementSchema = Schema.Struct({
  type: Schema.Literal("FindingCard"),
  props: Schema.Struct({
    findingId: BoundedIdentifierSchema,
    title: boundedString(512),
    summary: BoundedSummarySchema,
    significance: boundedString(2_000),
    ...ResearchEvidencePropsSchema,
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

const ReportSectionElementSchema = Schema.Struct({
  type: Schema.Literal("ReportSection"),
  props: Schema.Struct({
    sectionId: BoundedIdentifierSchema,
    title: boundedString(512),
    markdown: BoundedMarkdownSchema,
    ...ResearchEvidencePropsSchema,
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(32)),
});

const MetricGridElementSchema = Schema.Struct({
  type: Schema.Literal("MetricGrid"),
  props: Schema.Struct({}),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(64)),
});

const MetricElementSchema = Schema.Struct({
  type: Schema.Literal("Metric"),
  props: Schema.Struct({
    metricId: BoundedIdentifierSchema,
    label: BoundedLabelSchema,
    value: boundedString(512),
    unit: Schema.NullOr(boundedString(80)),
    context: boundedString(2_000),
    ...ResearchEvidencePropsSchema,
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

const DataTableElementSchema = Schema.Struct({
  type: Schema.Literal("DataTable"),
  props: Schema.Struct({
    tableId: BoundedIdentifierSchema,
    title: boundedString(512),
    columns: Schema.Array(BoundedLabelSchema).check(Schema.isMaxLength(24)),
    rows: Schema.Array(Schema.Array(boundedString(1_000)).check(Schema.isMaxLength(24))).check(
      Schema.isMaxLength(200),
    ),
    notes: Schema.NullOr(BoundedSummarySchema),
    ...ResearchEvidencePropsSchema,
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

const ChartElementSchema = Schema.Struct({
  type: Schema.Literal("Chart"),
  props: Schema.Struct({
    visualId: BoundedIdentifierSchema,
    visualType: Schema.Literals(["bar-chart", "line-chart"]),
    title: boundedString(512),
    dataRef: BoundedIdentifierSchema,
    narrative: boundedString(2_000),
    orientation: Schema.NullOr(Schema.Literals(["horizontal", "vertical"])),
    ...ResearchEvidencePropsSchema,
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

const DiagramElementSchema = Schema.Struct({
  type: Schema.Literal("Diagram"),
  props: Schema.Struct({
    visualId: BoundedIdentifierSchema,
    visualType: Schema.Literals(["flow", "timeline", "relationship", "matrix"]),
    title: boundedString(512),
    dataRef: BoundedIdentifierSchema,
    narrative: boundedString(2_000),
    orientation: Schema.NullOr(Schema.Literals(["horizontal", "vertical"])),
    ...ResearchEvidencePropsSchema,
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

const KnowledgeGraphPreviewElementSchema = Schema.Struct({
  type: Schema.Literal("KnowledgeGraphPreview"),
  props: Schema.Struct({
    nodeIds: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(128)),
    edgeIds: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(256)),
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

const RecommendationListElementSchema = Schema.Struct({
  type: Schema.Literal("RecommendationList"),
  props: Schema.Struct({
    items: Schema.Array(
      Schema.Struct({
        recommendationId: BoundedIdentifierSchema,
        action: boundedString(2_000),
        rationale: BoundedSummarySchema,
        priority: Schema.Literals(["high", "medium", "low"]),
        findingIds: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(64)),
        ...ResearchEvidencePropsSchema,
      }),
    ).check(Schema.isMaxLength(32)),
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

const RiskMatrixElementSchema = Schema.Struct({
  type: Schema.Literal("RiskMatrix"),
  props: Schema.Struct({
    items: Schema.Array(
      Schema.Struct({
        riskId: BoundedIdentifierSchema,
        risk: boundedString(2_000),
        likelihood: Schema.Literals(["high", "medium", "low"]),
        impact: Schema.Literals(["high", "medium", "low"]),
        mitigation: BoundedSummarySchema,
        ...ResearchEvidencePropsSchema,
      }),
    ).check(Schema.isMaxLength(32)),
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

const MethodologyElementSchema = Schema.Struct({
  type: Schema.Literal("Methodology"),
  props: Schema.Struct({
    content: BoundedSummarySchema,
    ...ResearchEvidencePropsSchema,
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

const LimitationsElementSchema = Schema.Struct({
  type: Schema.Literal("Limitations"),
  props: Schema.Struct({
    items: Schema.Array(boundedString(2_000)).check(Schema.isMaxLength(32)),
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

const FollowUpQuestionsElementSchema = Schema.Struct({
  type: Schema.Literal("FollowUpQuestions"),
  props: Schema.Struct({
    items: Schema.Array(
      Schema.Struct({
        questionId: BoundedIdentifierSchema,
        question: boundedString(2_000),
        reason: boundedString(2_000),
      }),
    ).check(Schema.isMaxLength(32)),
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

const SourceListElementSchema = Schema.Struct({
  type: Schema.Literal("SourceList"),
  props: Schema.Struct({
    sources: Schema.Array(ResearchSourceSchema).check(Schema.isMaxLength(200)),
  }),
  children: Schema.Array(BoundedIdentifierSchema).check(Schema.isMaxLength(16)),
});

export const ResearchReportElementSchema = Schema.Union([
  ReportRootElementSchema,
  ReportHeaderElementSchema,
  ExecutiveSummaryElementSchema,
  FindingGridElementSchema,
  FindingCardElementSchema,
  ReportSectionElementSchema,
  MetricGridElementSchema,
  MetricElementSchema,
  DataTableElementSchema,
  ChartElementSchema,
  DiagramElementSchema,
  KnowledgeGraphPreviewElementSchema,
  RecommendationListElementSchema,
  RiskMatrixElementSchema,
  MethodologyElementSchema,
  LimitationsElementSchema,
  FollowUpQuestionsElementSchema,
  SourceListElementSchema,
]);

/** Renderer projection; research semantics remain in ResearchBundleV1. */
const ResearchReportElementsSchema = Schema.Record(
  Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(180)),
  ResearchReportElementSchema,
).check(
  Schema.makeFilter(
    (elements) => Object.keys(elements).length <= 512 || "must contain at most 512 elements",
  ),
);

export const ResearchReportViewSpecV1Schema = Schema.Struct({
  version: Schema.Literal("research-report-view-v1"),
  root: BoundedIdentifierSchema,
  elements: ResearchReportElementsSchema,
}).check(Schema.makeFilter((value) => encodedJsonFits(value, RESEARCH_REPORT_MAX_ENCODED_BYTES)));

/** Browser-safe authorized read request. Object storage identity remains server-only. */
export const AuthorizedResearchReportReadRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  artifactRevisionId: ResearchIdentifierSchema,
});

export const AuthorizedResearchReportContentSchema = Schema.Struct({
  researchRunId: ResearchRunIdSchema,
  artifactRevisionId: ResearchIdentifierSchema,
  bundle: ResearchBundleV1Schema,
  basisIndex: ResearchBasisIndexSchema,
  report: ResearchReportViewSpecV1Schema,
}).check(
  Schema.makeFilter((value) =>
    encodedJsonFits(value, AUTHORIZED_RESEARCH_REPORT_MAX_ENCODED_BYTES),
  ),
);

export const AuthorizedResearchReportReadResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("ready"),
    content: AuthorizedResearchReportContentSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("pending"),
    researchRunId: ResearchRunIdSchema,
    artifactRevisionId: ResearchIdentifierSchema,
    retryAfterMs: Schema.Int.check(
      Schema.isGreaterThanOrEqualTo(1_000),
      Schema.isLessThanOrEqualTo(30_000),
    ),
  }),
]);

export type ResearchRunId = typeof ResearchRunIdSchema.Type;
export type ResearchReportRevisionId = typeof ResearchReportRevisionIdSchema.Type;
export type ResearchProcessor = typeof ResearchProcessorSchema.Type;
export type ResearchApplicationState = typeof ResearchApplicationStateSchema.Type;
export type ParallelRunState = typeof ParallelRunStateSchema.Type;
export type ResearchProviderSubmissionState = typeof ResearchProviderSubmissionStateSchema.Type;
export type ResearchCreditReservationState = typeof ResearchCreditReservationStateSchema.Type;
export type ResearchSourcePolicy = typeof ResearchSourcePolicySchema.Type;
export type StartResearchResult = typeof StartResearchResultSchema.Type;
export type StartResearchResultEncoded = typeof StartResearchResultSchema.Encoded;
export type ResearchBundleV1 = typeof ResearchBundleV1Schema.Type;
export type ResearchBundleV1Encoded = typeof ResearchBundleV1Schema.Encoded;
export type ResearchConfidence = typeof ConfidenceSchema.Type;
export type ResearchSource = typeof ResearchSourceSchema.Type;
export type ResearchCitationLocator = typeof ResearchCitationLocatorSchema.Type;
export type ResearchIndexedCitation = typeof ResearchIndexedCitationSchema.Type;
export type ResearchBasisIndex = typeof ResearchBasisIndexSchema.Type;
export type ResearchBasisIndexEncoded = typeof ResearchBasisIndexSchema.Encoded;
export type ResearchDiagramModel = typeof ResearchDiagramModelSchema.Type;
export type ResearchDiagramModelEncoded = typeof ResearchDiagramModelSchema.Encoded;
export type ResearchReportElement = typeof ResearchReportElementSchema.Type;
export type ResearchReportViewSpecV1 = typeof ResearchReportViewSpecV1Schema.Type;
export type ResearchReportViewSpecV1Encoded = typeof ResearchReportViewSpecV1Schema.Encoded;
export type AuthorizedResearchReportReadRequest =
  typeof AuthorizedResearchReportReadRequestSchema.Type;
export type AuthorizedResearchReportReadRequestEncoded =
  typeof AuthorizedResearchReportReadRequestSchema.Encoded;
export type AuthorizedResearchReportContent = typeof AuthorizedResearchReportContentSchema.Type;
export type AuthorizedResearchReportContentEncoded =
  typeof AuthorizedResearchReportContentSchema.Encoded;
export type AuthorizedResearchReportReadResult =
  typeof AuthorizedResearchReportReadResultSchema.Type;
export type AuthorizedResearchReportReadResultEncoded =
  typeof AuthorizedResearchReportReadResultSchema.Encoded;

/** Best-effort source document; provider adapters must still audit and compile it fail-closed. */
export const ResearchBundleV1JsonSchema = Schema.toJsonSchemaDocument(ResearchBundleV1Schema, {
  additionalProperties: false,
});

/** Durable deep-research control-plane input. Provider SKU selection remains server-owned. */
export const PrepareResearchRunRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  projectId: ResearchIdentifierSchema,
  threadId: ResearchIdentifierSchema,
  agentRunId: ResearchIdentifierSchema,
  agentAttemptId: ResearchIdentifierSchema,
  attemptGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  operationId: ResearchIdentifierSchema,
  approvalId: ResearchIdentifierSchema,
  toolCallId: ResearchIdentifierSchema,
  inputDigest: Schema.String.check(
    Schema.makeFilter((value) => /^[a-f0-9]{64}$/.test(value) || "must be lowercase SHA-256"),
  ),
  approvalExpiresAt: ResearchSafeIntegerSchema,
  actorUserId: ResearchIdentifierSchema,
  parentResearchRunId: Schema.optionalKey(ResearchRunIdSchema),
  title: ResearchTitleSchema,
  question: ResearchQuestionSchema,
  mode: ResearchProductModeSchema,
  processor: ResearchProcessorSchema,
  sourcePolicy: ResearchSourcePolicySchema,
  taskSpecVersion: ResearchVersionSchema,
  inputRevision: ResearchVersionSchema,
  reservedProviderCostMicros: ResearchSafeIntegerSchema,
  reservedBillableCostMicros: ResearchSafeIntegerSchema,
  currency: ResearchCurrencySchema,
  priceBookVersion: ResearchVersionSchema,
});

export const PrepareResearchRunResultSchema = Schema.Struct({
  researchRunId: ResearchRunIdSchema,
  reportArtifactId: ResearchIdentifierSchema,
  creditReservationId: ResearchIdentifierSchema,
  applicationStatus: ResearchApplicationStateSchema,
  approval: Schema.Struct({
    approvalId: ResearchIdentifierSchema,
    status: Schema.Literals(["pending", "approved", "denied", "expired"]),
    title: ResearchTitleSchema,
    question: ResearchQuestionSchema,
    maximumBillableCostMicros: ResearchSafeIntegerSchema,
    currency: ResearchCurrencySchema,
    priceBookVersion: ResearchVersionSchema,
    expiresAt: ResearchSafeIntegerSchema,
    consequence: Schema.Literal("One approved claim may create one billable Parallel Task."),
  }),
  executionMode: Schema.Literal("proposal"),
  providerStartAuthorized: Schema.Literal(false),
});

export const DecideResearchApprovalRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  approvalId: ResearchIdentifierSchema,
  decision: Schema.Literals(["approved", "denied"]),
});
export const DecideResearchApprovalResultSchema = Schema.Struct({
  approvalId: ResearchIdentifierSchema,
  status: Schema.Literals(["approved", "denied", "expired"]),
  researchRunId: ResearchRunIdSchema,
  creditReservationStatus: ResearchCreditReservationStateSchema,
});

export const ResearchApprovalProjectionSchema = Schema.Struct({
  approvalId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  reportArtifactId: ResearchIdentifierSchema,
  mode: ResearchProductModeSchema,
  status: Schema.Literals(["pending", "approved", "denied", "expired"]),
  title: ResearchTitleSchema,
  question: ResearchQuestionSchema,
  maximumBillableCostMicros: ResearchSafeIntegerSchema,
  currency: ResearchCurrencySchema,
  priceBookVersion: ResearchVersionSchema,
  expiresAt: ResearchSafeIntegerSchema,
  target: Schema.Literal("Parallel Task API"),
  disclosure: Schema.Literal(
    "Research question, output contract, and selected source-domain policy.",
  ),
  billingCondition: Schema.Literal(
    "PapersFlow settles the charge only when Parallel reports successful completion.",
  ),
  sourcePolicy: ResearchSourcePolicySchema,
  consequence: Schema.Literal("One approved claim may create one billable Parallel Task."),
});

export const ListPendingResearchApprovalsRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
});
export const ListPendingResearchApprovalsResultSchema = Schema.Array(
  ResearchApprovalProjectionSchema,
).check(Schema.isMaxLength(100));

export const ClaimResearchProviderSubmissionRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  claimOwner: ResearchIdentifierSchema,
  leaseDurationMs: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1_000),
    Schema.isLessThanOrEqualTo(120_000),
  ),
});

const ResearchSubmissionCostSnapshotSchema = Schema.Struct({
  operationId: ResearchIdentifierSchema,
  processor: ResearchProcessorSchema,
  reservedProviderCostMicros: ResearchSafeIntegerSchema,
  reservedBillableCostMicros: ResearchSafeIntegerSchema,
  currency: ResearchCurrencySchema,
  priceBookVersion: ResearchVersionSchema,
});

export const ClaimResearchProviderSubmissionResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("claimed"),
    researchRunId: ResearchRunIdSchema,
    claimOwner: ResearchIdentifierSchema,
    submissionGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
    leaseUntil: ResearchSafeIntegerSchema,
    providerStartAuthorized: Schema.Literal(true),
    cost: ResearchSubmissionCostSnapshotSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("blocked"),
    reason: Schema.Literal("LIVE_START_DISABLED"),
  }),
  Schema.Struct({
    status: Schema.Literal("rejected"),
    reason: Schema.Literals(["APPROVAL_REQUIRED", "APPROVAL_DENIED", "APPROVAL_EXPIRED"]),
  }),
  Schema.Struct({ status: Schema.Literal("busy"), leaseUntil: ResearchSafeIntegerSchema }),
  Schema.Struct({
    status: Schema.Literal("already-attached"),
    parallelRunId: ResearchIdentifierSchema,
    interactionId: ResearchIdentifierSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("outcome-unknown"),
    operationId: ResearchIdentifierSchema,
  }),
  Schema.Struct({ status: Schema.Literal("not-accepted") }),
]);

export const BeginResearchProviderSubmissionRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  claimOwner: ResearchIdentifierSchema,
  submissionGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
});
export const BeginResearchProviderSubmissionResultSchema = Schema.Struct({
  status: Schema.Literal("dispatching"),
  operationId: ResearchIdentifierSchema,
  submissionGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  cost: ResearchSubmissionCostSnapshotSchema,
});

export const MarkResearchProviderSubmissionOutcomeRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  claimOwner: ResearchIdentifierSchema,
  submissionGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  outcome: Schema.Literals(["not-accepted", "outcome-unknown"]),
  errorCode: boundedString(120, 1),
  errorMessage: boundedString(500, 1),
});
export const MarkResearchProviderSubmissionOutcomeResultSchema = Schema.Struct({
  status: Schema.Literals(["not-accepted", "outcome-unknown", "already-attached"]),
  automaticRetryAllowed: Schema.Literal(false),
});

export const ReleaseResearchProviderSubmissionClaimRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  claimOwner: ResearchIdentifierSchema,
  submissionGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
});
export const ReleaseResearchProviderSubmissionClaimResultSchema = Schema.Struct({
  status: Schema.Literal("released"),
});

export const AttachResearchProviderRunRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  claimOwner: ResearchIdentifierSchema,
  submissionGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  parallelRunId: ResearchIdentifierSchema,
  interactionId: ResearchIdentifierSchema,
  providerStatus: Schema.Literals(["queued", "running"]),
});
export const AttachResearchProviderRunResultSchema = Schema.Struct({
  applicationStatus: ResearchApplicationStateSchema,
});

export const RESEARCH_PROVIDER_ACTIVITY_MAX_SEARCHES = 24;
export const RESEARCH_PROVIDER_ACTIVITY_MAX_PROVISIONAL_SOURCES = 50;
export const RESEARCH_PROVIDER_ACTIVITY_MAX_SEARCH_TEXT_LENGTH = 512;
export const RESEARCH_PROVIDER_ACTIVITY_OBSERVER_MIN_LEASE_DURATION_MS = 1_000;
export const RESEARCH_PROVIDER_ACTIVITY_OBSERVER_MAX_LEASE_DURATION_MS = 5 * 60_000;

export const ResearchProviderActivityPhaseSchema = Schema.Literals([
  "planning",
  "exploring",
  "synthesizing",
]);
export const ResearchProviderActivityPhaseStatusSchema = Schema.Literals([
  "pending",
  "active",
  "observed",
]);
export const ResearchProviderActivityEventSubtypeSchema = Schema.Literals([
  "task_run.progress_msg.plan",
  "task_run.progress_msg.search",
  "task_run.progress_msg.result",
  "task_run.progress_stats",
  "task_run.state",
]);

function validateResearchProviderTimestamp(value: string): boolean | string {
  const match =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-](\d{2}):(\d{2}))$/u.exec(
      value,
    );
  if (!match) return "must be an RFC 3339 timestamp";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[7] === undefined ? 0 : Number(match[7]);
  const offsetMinute = match[8] === undefined ? 0 : Number(match[8]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = month === 2 ? (leapYear ? 29 : 28) : [4, 6, 9, 11].includes(month) ? 30 : 31;
  return (
    (month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= daysInMonth &&
      hour <= 23 &&
      minute <= 59 &&
      second <= 59 &&
      offsetHour <= 23 &&
      offsetMinute <= 59) ||
    "must be an RFC 3339 timestamp"
  );
}

const ResearchProviderTimestampSchema = Schema.String.check(
  Schema.isMinLength(20),
  Schema.isMaxLength(40),
  Schema.makeFilter(validateResearchProviderTimestamp),
);
const NullableResearchProviderTimestampSchema = Schema.NullOr(ResearchProviderTimestampSchema);
const ResearchProviderActivityPhaseStatusFields = {
  status: ResearchProviderActivityPhaseStatusSchema,
  lastProviderTimestamp: NullableResearchProviderTimestampSchema,
};

export const ResearchProviderActivityPhasesSchema = Schema.Struct({
  planning: Schema.Struct({
    ...ResearchProviderActivityPhaseStatusFields,
  }),
  exploring: Schema.Struct({
    ...ResearchProviderActivityPhaseStatusFields,
  }),
  synthesizing: Schema.Struct({
    ...ResearchProviderActivityPhaseStatusFields,
  }),
});

export const ResearchProviderObservedSearchSchema = Schema.Struct({
  kind: Schema.Literals(["objective", "query"]),
  text: Schema.String.check(
    Schema.isMinLength(1),
    Schema.isMaxLength(RESEARCH_PROVIDER_ACTIVITY_MAX_SEARCH_TEXT_LENGTH),
    Schema.makeFilter(
      (value) =>
        (value === value.trim() && !/[\r\n]/u.test(value)) ||
        "must be canonical trimmed single-line text",
    ),
  ),
  providerTimestamp: NullableResearchProviderTimestampSchema,
  occurrenceOrdinal: ResearchSafeIntegerSchema,
  replaySequence: ResearchSafeIntegerSchema.check(Schema.isGreaterThanOrEqualTo(1)),
});

export const ResearchProviderSourceStatsSchema = Schema.Struct({
  progress: Schema.NullOr(Schema.Finite.check(Schema.isBetween({ minimum: 0, maximum: 100 }))),
  considered: Schema.NullOr(ResearchSafeIntegerSchema),
  read: Schema.NullOr(ResearchSafeIntegerSchema),
  provisionalSources: Schema.Array(
    Schema.Struct({
      url: ResearchPublicHttpUrlSchema,
      domain: ResearchDomainSchema,
    }),
  ).check(Schema.isMaxLength(RESEARCH_PROVIDER_ACTIVITY_MAX_PROVISIONAL_SOURCES)),
});

export const ResearchProviderReplayPositionSchema = Schema.Struct({
  eventSubtype: ResearchProviderActivityEventSubtypeSchema,
  providerTimestamp: NullableResearchProviderTimestampSchema,
  occurrenceOrdinal: ResearchSafeIntegerSchema,
  replaySequence: ResearchSafeIntegerSchema,
});

const ResearchProviderActivityFields = {
  version: Schema.Literal("research-provider-activity-v1"),
  activePhase: Schema.NullOr(ResearchProviderActivityPhaseSchema),
  phases: ResearchProviderActivityPhasesSchema,
  searches: Schema.Array(ResearchProviderObservedSearchSchema).check(
    Schema.isMaxLength(RESEARCH_PROVIDER_ACTIVITY_MAX_SEARCHES),
  ),
  sourceStats: ResearchProviderSourceStatsSchema,
  replayPosition: ResearchProviderReplayPositionSchema,
};

const ResearchProviderActivityCandidateBaseSchema = Schema.Struct(ResearchProviderActivityFields);

function validateResearchProviderActivity(
  value: typeof ResearchProviderActivityCandidateBaseSchema.Type,
): boolean | string {
  const activePhases = (["planning", "exploring", "synthesizing"] as const).filter(
    (phase) => value.phases[phase].status === "active",
  );
  if (
    (value.activePhase === null && activePhases.length !== 0) ||
    (value.activePhase !== null &&
      (activePhases.length !== 1 || activePhases[0] !== value.activePhase))
  ) {
    return "activePhase must identify the only active phase";
  }
  for (const phase of ["planning", "exploring", "synthesizing"] as const) {
    if (
      value.phases[phase].status === "pending" &&
      value.phases[phase].lastProviderTimestamp !== null
    ) {
      return "pending phases must not have a provider timestamp";
    }
  }
  let priorSearchReplaySequence = 0;
  for (const search of value.searches) {
    if (search.replaySequence < priorSearchReplaySequence) {
      return "observed searches must be ordered by replaySequence";
    }
    priorSearchReplaySequence = search.replaySequence;
  }
  const provisionalSourceUrls = new Set<string>();
  for (const source of value.sourceStats.provisionalSources) {
    if (provisionalSourceUrls.has(source.url)) {
      return "provisional source URLs must be unique";
    }
    provisionalSourceUrls.add(source.url);
    const sourceUrl = Schema.decodeSync(Schema.URLFromString)(source.url);
    if (sourceUrl.hostname.toLowerCase() !== source.domain) {
      return "provisional source domain must match its URL hostname";
    }
  }
  return (
    value.searches.every(
      ({ replaySequence }) => replaySequence <= value.replayPosition.replaySequence,
    ) || "observed search replaySequence must not exceed the replay position"
  );
}

export const ResearchProviderActivityCandidateSchema =
  ResearchProviderActivityCandidateBaseSchema.check(
    Schema.makeFilter(validateResearchProviderActivity),
  );

export const areResearchProviderActivityCandidatesEquivalent = Schema.toEquivalence(
  ResearchProviderActivityCandidateSchema,
);

export const ResearchProviderActivitySnapshotSchema = Schema.Struct({
  ...ResearchProviderActivityFields,
  revision: ResearchSafeIntegerSchema.check(Schema.isGreaterThanOrEqualTo(1)),
  updatedAt: ResearchSafeIntegerSchema,
}).check(Schema.makeFilter(validateResearchProviderActivity));

export const ClaimResearchProviderActivityObserverRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  parallelRunId: ResearchIdentifierSchema,
  observerOwner: ResearchIdentifierSchema,
  leaseDurationMs: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(RESEARCH_PROVIDER_ACTIVITY_OBSERVER_MIN_LEASE_DURATION_MS),
    Schema.isLessThanOrEqualTo(RESEARCH_PROVIDER_ACTIVITY_OBSERVER_MAX_LEASE_DURATION_MS),
  ),
});
export const ClaimResearchProviderActivityObserverResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("claimed"),
    observerGeneration: ResearchSafeIntegerSchema.check(Schema.isGreaterThanOrEqualTo(1)),
    leaseUntil: ResearchSafeIntegerSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("busy"),
    leaseUntil: ResearchSafeIntegerSchema,
  }),
  Schema.Struct({ status: Schema.Literal("terminal") }),
]);

export const ReleaseResearchProviderActivityObserverRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  parallelRunId: ResearchIdentifierSchema,
  observerOwner: ResearchIdentifierSchema,
  observerGeneration: ResearchSafeIntegerSchema.check(Schema.isGreaterThanOrEqualTo(1)),
});
export const RenewResearchProviderActivityObserverRequestSchema =
  ReleaseResearchProviderActivityObserverRequestSchema;
export const RenewResearchProviderActivityObserverResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("renewed"),
    leaseUntil: ResearchSafeIntegerSchema,
  }),
  Schema.Struct({ status: Schema.Literal("stale-observer") }),
  Schema.Struct({ status: Schema.Literal("terminal") }),
]);
export const ReleaseResearchProviderActivityObserverResultSchema = Schema.Struct({
  status: Schema.Literals(["released", "stale-observer", "terminal"]),
});

export const ReplaceResearchProviderActivityRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  parallelRunId: ResearchIdentifierSchema,
  observerOwner: ResearchIdentifierSchema,
  observerGeneration: ResearchSafeIntegerSchema.check(Schema.isGreaterThanOrEqualTo(1)),
  updateOrdinal: ResearchSafeIntegerSchema.check(Schema.isGreaterThanOrEqualTo(1)),
  candidate: ResearchProviderActivityCandidateSchema,
});
export const ReplaceResearchProviderActivityResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("recorded"),
    revision: ResearchSafeIntegerSchema.check(Schema.isGreaterThanOrEqualTo(1)),
    updatedAt: ResearchSafeIntegerSchema,
  }),
  Schema.Struct({
    status: Schema.Literal("unchanged"),
    revision: ResearchSafeIntegerSchema.check(Schema.isGreaterThanOrEqualTo(1)),
    updatedAt: ResearchSafeIntegerSchema,
  }),
  Schema.Struct({ status: Schema.Literal("terminal") }),
  Schema.Struct({ status: Schema.Literal("stale-observer") }),
]);

export const ObserveResearchProviderTerminalRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  parallelRunId: ResearchIdentifierSchema,
  providerStatus: Schema.Literals(["completed", "failed", "cancelled"]),
});
export const ObserveResearchProviderTerminalResultSchema = Schema.Struct({
  applicationStatus: ResearchApplicationStateSchema,
});

export const ClaimResearchFinalizationRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  leaseOwner: ResearchIdentifierSchema,
  leaseDurationMs: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1_000),
    Schema.isLessThanOrEqualTo(5 * 60_000),
  ),
});

export const ResearchStorageObjectKindSchema = Schema.Literals([
  "raw-result",
  "raw-basis",
  "canonical-bundle",
  "basis-index",
  "report-spec",
]);

export const RESEARCH_STORAGE_OBJECT_MAX_BYTES = 16 * 1_024 * 1_024;
export const RESEARCH_STORAGE_CLEANUP_BATCH_SIZE = 20;
export const RESEARCH_STORAGE_CLEANUP_MAX_ATTEMPTS = 5;

export const ResearchStorageObjectClaimSchema = Schema.Struct({
  kind: ResearchStorageObjectKindSchema,
  storageObjectId: ResearchIdentifierSchema,
  objectKey: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2_048)),
});

export const ClaimResearchFinalizationResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("claimed"),
    researchRunId: ResearchRunIdSchema,
    leaseOwner: ResearchIdentifierSchema,
    leaseGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
    cancellationGeneration: ResearchSafeIntegerSchema,
    leaseUntil: ResearchSafeIntegerSchema,
    objects: Schema.Array(ResearchStorageObjectClaimSchema).check(
      Schema.isMinLength(5),
      Schema.isMaxLength(5),
      Schema.makeFilter(
        (objects) =>
          new Set(objects.map(({ kind }) => kind)).size === 5 ||
          "must contain one claim for each research object kind",
      ),
    ),
  }),
  Schema.Struct({
    status: Schema.Literal("busy"),
    leaseUntil: ResearchSafeIntegerSchema,
  }),
  Schema.Struct({ status: Schema.Literal("not-ready") }),
  Schema.Struct({
    status: Schema.Literal("completed"),
    artifactRevisionId: ResearchIdentifierSchema,
  }),
]);

export const ReleaseResearchFinalizationLeaseRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  leaseOwner: ResearchIdentifierSchema,
  leaseGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  cancellationGeneration: ResearchSafeIntegerSchema,
});
export const ReleaseResearchFinalizationLeaseResultSchema = Schema.Struct({
  status: Schema.Literal("released"),
});

export const ResearchStorageObjectReservationSchema = Schema.Struct({
  kind: ResearchStorageObjectKindSchema,
  storageObjectId: ResearchIdentifierSchema,
  byteSize: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(RESEARCH_STORAGE_OBJECT_MAX_BYTES),
  ),
  sha256: Schema.String.check(
    Schema.makeFilter((value) => /^[a-f0-9]{64}$/.test(value) || "must be lowercase SHA-256"),
  ),
});

export const ReserveResearchFinalizationObjectsRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  leaseOwner: ResearchIdentifierSchema,
  leaseGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  cancellationGeneration: ResearchSafeIntegerSchema,
  objects: Schema.Array(ResearchStorageObjectReservationSchema).check(
    Schema.isMinLength(5),
    Schema.isMaxLength(5),
    Schema.makeFilter(
      (objects) =>
        new Set(objects.map(({ kind }) => kind)).size === 5 ||
        "must contain one reservation for each research object kind",
    ),
  ),
});
export const ReserveResearchFinalizationObjectsResultSchema = Schema.Struct({
  status: Schema.Literal("reserved"),
});

const ResearchCostReconciliationFields = {
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  reconciliationId: ResearchIdentifierSchema,
  submissionGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  settledProviderCostMicros: Schema.Literal(0),
  billableCostMicros: Schema.Literal(0),
  currency: ResearchCurrencySchema,
  priceBookVersion: ResearchVersionSchema,
};

export const ReconcileResearchCostsRequestSchema = Schema.Union([
  Schema.Struct({
    ...ResearchCostReconciliationFields,
    providerOutcome: Schema.Literal("accepted-active"),
    parallelRunId: ResearchIdentifierSchema,
    interactionId: ResearchIdentifierSchema,
    providerStatus: Schema.Literals(["queued", "running"]),
  }),
  Schema.Struct({
    ...ResearchCostReconciliationFields,
    providerOutcome: Schema.Literal("not-accepted"),
  }),
]);
export const ReconcileResearchCostsResultSchema = Schema.Struct({
  status: Schema.Literals(["attached", "released"]),
  settledProviderCostMicros: ResearchSafeIntegerSchema,
  billableCostMicros: ResearchSafeIntegerSchema,
  releasedCreditMicros: ResearchSafeIntegerSchema,
});

export const GetResearchRecoveryContextRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
});
export const GetResearchRecoveryContextResultSchema = Schema.Struct({
  researchRunId: ResearchRunIdSchema,
  workspaceId: ResearchIdentifierSchema,
  projectId: ResearchIdentifierSchema,
  title: ResearchTitleSchema,
  question: ResearchQuestionSchema,
  mode: ResearchProductModeSchema,
  processor: ResearchProcessorSchema,
  sourcePolicy: ResearchSourcePolicySchema,
  taskSpecVersion: ResearchVersionSchema,
  inputRevision: ResearchVersionSchema,
  applicationStatus: ResearchApplicationStateSchema,
  submissionStatus: ResearchProviderSubmissionStateSchema,
  recoveryAction: Schema.Literals([
    "none",
    "live-start-blocked",
    "claim-submission",
    "inspect-ambiguous-submission",
    "observe-existing-provider-run",
    "finalize-existing-provider-run",
    "reconcile-cost",
  ]),
  automaticCreateRetryAllowed: Schema.Literal(false),
  parallelRunId: Schema.optionalKey(ResearchIdentifierSchema),
  interactionId: Schema.optionalKey(ResearchIdentifierSchema),
  reservation: Schema.Struct({
    status: ResearchCreditReservationStateSchema,
    creditPeriodKey: ResearchVersionSchema,
    periodStartAt: ResearchSafeIntegerSchema,
    periodEndAt: ResearchSafeIntegerSchema,
    reservedProviderCostMicros: ResearchSafeIntegerSchema,
    reservedBillableCostMicros: ResearchSafeIntegerSchema,
    settledProviderCostMicros: ResearchSafeIntegerSchema,
    billableCostMicros: ResearchSafeIntegerSchema,
    releasedCreditMicros: ResearchSafeIntegerSchema,
    currency: ResearchCurrencySchema,
    priceBookVersion: ResearchVersionSchema,
  }),
});

export const ListRecoverableResearchRunsRequestSchema = Schema.Struct({
  limit: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 100 })),
});
export const ListRecoverableResearchRunsResultSchema = Schema.Array(
  GetResearchRecoveryContextResultSchema,
).check(Schema.isMaxLength(100));

const AuthorizedResearchStorageObjectSchema = Schema.Struct({
  kind: Schema.Literals(["canonical-bundle", "basis-index", "report-spec"]),
  storageObjectId: ResearchIdentifierSchema,
  objectKey: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2_048)),
  mediaType: Schema.Literal("application/json"),
  byteSize: ResearchSafeIntegerSchema,
  sha256: Schema.String.check(
    Schema.makeFilter((value) => /^[a-f0-9]{64}$/.test(value) || "must be lowercase SHA-256"),
  ),
  etag: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(512)),
});

/** Internal authorization result. Object identities must never cross the browser response. */
export const AuthorizeResearchReportReadRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  artifactRevisionId: ResearchIdentifierSchema,
  actorUserId: ResearchIdentifierSchema,
});
export const AuthorizeResearchReportReadResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("ready"),
    workspaceId: ResearchIdentifierSchema,
    projectId: ResearchIdentifierSchema,
    researchRunId: ResearchRunIdSchema,
    artifactRevisionId: ResearchIdentifierSchema,
    objects: Schema.Array(AuthorizedResearchStorageObjectSchema).check(
      Schema.isMinLength(3),
      Schema.isMaxLength(3),
      Schema.makeFilter(
        (objects) =>
          new Set(objects.map(({ kind }) => kind)).size === 3 ||
          "must contain one authorized canonical object of each kind",
      ),
    ),
  }),
  Schema.Struct({
    status: Schema.Literal("pending"),
    researchRunId: ResearchRunIdSchema,
    artifactRevisionId: ResearchIdentifierSchema,
    retryAfterMs: Schema.Int.check(Schema.isBetween({ minimum: 1_000, maximum: 30_000 })),
  }),
]);

export const ResearchStorageObjectCommitSchema = Schema.Struct({
  kind: ResearchStorageObjectKindSchema,
  storageObjectId: ResearchIdentifierSchema,
  mediaType: Schema.Literal("application/json"),
  byteSize: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(RESEARCH_STORAGE_OBJECT_MAX_BYTES),
  ),
  sha256: Schema.String.check(
    Schema.makeFilter((value) => /^[a-f0-9]{64}$/.test(value) || "must be lowercase SHA-256"),
  ),
  etag: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(512)),
});

export const ResearchSourceProjectionSchema = Schema.Struct({
  ...ResearchSourceSchema.fields,
  ordinal: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
});

export const CommitResearchFinalizationRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  leaseOwner: ResearchIdentifierSchema,
  leaseGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  cancellationGeneration: ResearchSafeIntegerSchema,
  objects: Schema.Array(ResearchStorageObjectCommitSchema).check(
    Schema.isMinLength(5),
    Schema.isMaxLength(5),
    Schema.makeFilter(
      (objects) =>
        new Set(objects.map(({ kind }) => kind)).size === 5 ||
        "must contain one commit for each research object kind",
    ),
  ),
  sources: Schema.Array(ResearchSourceProjectionSchema).check(Schema.isMaxLength(200)),
  bundleVersion: Schema.Literal("research-bundle-v1"),
  basisIndexVersion: Schema.Literal("research-basis-index-v1"),
  rendererVersion: Schema.Literal("research-report-view-v1"),
  settledProviderCostMicros: ResearchSafeIntegerSchema,
  billableCostMicros: ResearchSafeIntegerSchema,
  currency: ResearchCurrencySchema,
  priceBookVersion: ResearchVersionSchema,
});

export const CommitResearchFinalizationResultSchema = Schema.Struct({
  status: Schema.Literal("ready"),
  artifactRevisionId: ResearchIdentifierSchema,
});

export const FailResearchFinalizationRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  leaseOwner: ResearchIdentifierSchema,
  leaseGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  cancellationGeneration: ResearchSafeIntegerSchema,
  errorCode: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
  errorMessage: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500)),
});
export const FailResearchFinalizationResultSchema = Schema.Struct({
  status: Schema.Literal("failed"),
});

export const ClaimResearchStorageCleanupRequestSchema = Schema.Struct({
  owner: ResearchIdentifierSchema,
  limit: Schema.Int.check(
    Schema.isBetween({ minimum: 1, maximum: RESEARCH_STORAGE_CLEANUP_BATCH_SIZE }),
  ),
});
export const ResearchStorageCleanupClaimSchema = Schema.Struct({
  cleanupJobId: ResearchIdentifierSchema,
  workspaceId: ResearchIdentifierSchema,
  projectId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  kind: ResearchStorageObjectKindSchema,
  storageObjectId: ResearchIdentifierSchema,
  objectKey: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2_048)),
  attempt: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(RESEARCH_STORAGE_CLEANUP_MAX_ATTEMPTS),
  ),
  leaseExpiresAt: ResearchSafeIntegerSchema,
});
export const ClaimResearchStorageCleanupResultSchema = Schema.Array(
  ResearchStorageCleanupClaimSchema,
).check(Schema.isMaxLength(RESEARCH_STORAGE_CLEANUP_BATCH_SIZE));

const ResearchStorageCleanupResultFields = {
  cleanupJobId: ResearchIdentifierSchema,
  owner: ResearchIdentifierSchema,
  attempt: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(RESEARCH_STORAGE_CLEANUP_MAX_ATTEMPTS),
  ),
};
export const RecordResearchStorageCleanupRequestSchema = Schema.Union([
  Schema.Struct({
    ...ResearchStorageCleanupResultFields,
    outcome: Schema.Literal("confirmed"),
  }),
  Schema.Struct({
    ...ResearchStorageCleanupResultFields,
    outcome: Schema.Literal("retry"),
    errorCode: Schema.Literal("RESEARCH_STORAGE_DELETE_FAILED"),
  }),
]);
export const RecordResearchStorageCleanupResultSchema = Schema.Struct({
  cleanupJobId: ResearchIdentifierSchema,
  status: Schema.Literals(["pending", "completed", "abandoned"]),
  attempts: ResearchSafeIntegerSchema,
});

export const CancelResearchRunRequestSchema = Schema.Struct({
  workspaceId: ResearchIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  actorUserId: ResearchIdentifierSchema,
});
export const CancelResearchRunResultSchema = Schema.Struct({
  status: Schema.Literal("cancelled"),
  providerCancellation: Schema.Literal("unavailable"),
});

export const RESEARCH_RUN_DETAIL_MAX_MILESTONES = 40;

export const ResearchMilestoneProjectionSchema = Schema.Struct({
  eventId: ResearchIdentifierSchema,
  kind: Schema.Literals(["tool.started", "tool.completed", "tool.failed"]),
  status: Schema.Literals(["running", "succeeded", "failed", "cancelled"]),
  title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  occurredAt: ResearchSafeIntegerSchema,
});

export const ResearchRunProjectionSchema = Schema.Struct({
  researchRunId: ResearchRunIdSchema,
  workspaceId: ResearchIdentifierSchema,
  projectId: ResearchIdentifierSchema,
  threadId: ResearchIdentifierSchema,
  reportArtifactId: ResearchIdentifierSchema,
  requestedBy: ResearchIdentifierSchema,
  title: ResearchTitleSchema,
  question: ResearchQuestionSchema,
  mode: ResearchProductModeSchema,
  applicationStatus: ResearchApplicationStateSchema,
  submissionStatus: ResearchProviderSubmissionStateSchema,
  creditReservationStatus: ResearchCreditReservationStateSchema,
  providerStatus: Schema.optionalKey(ParallelRunStateSchema),
  sourceCount: ResearchSafeIntegerSchema,
  createdAt: ResearchSafeIntegerSchema,
  updatedAt: ResearchSafeIntegerSchema,
  startedAt: Schema.optionalKey(ResearchSafeIntegerSchema),
  completedAt: Schema.optionalKey(ResearchSafeIntegerSchema),
  failedAt: Schema.optionalKey(ResearchSafeIntegerSchema),
  cancelledAt: Schema.optionalKey(ResearchSafeIntegerSchema),
  artifactRevisionId: Schema.optionalKey(ResearchIdentifierSchema),
  errorCode: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(120))),
  errorMessage: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(500))),
});

export const ResearchBillableCostProjectionSchema = Schema.Struct({
  reservedBillableCostMicros: ResearchSafeIntegerSchema,
  billableCostMicros: ResearchSafeIntegerSchema,
  releasedCreditMicros: ResearchSafeIntegerSchema,
  currency: ResearchCurrencySchema,
});

const ResearchReportProjectionBase = {
  artifactId: ResearchIdentifierSchema,
  createdAt: ResearchSafeIntegerSchema,
};

export const ResearchReportProjectionSchema = Schema.Union([
  Schema.Struct({
    ...ResearchReportProjectionBase,
    status: Schema.Literal("ready"),
    artifactRevisionId: ResearchIdentifierSchema,
    bundleVersion: ResearchVersionSchema,
    basisIndexVersion: ResearchVersionSchema,
    rendererVersion: ResearchVersionSchema,
    committedAt: ResearchSafeIntegerSchema,
  }),
  Schema.Struct({
    ...ResearchReportProjectionBase,
    status: Schema.Literals(["creating", "failed", "cancelled"]),
  }),
]);

export const ResearchRunDetailSchema = Schema.Struct({
  run: ResearchRunProjectionSchema,
  providerActivity: Schema.optionalKey(ResearchProviderActivitySnapshotSchema),
  sourcePolicy: ResearchSourcePolicySchema,
  permissions: Schema.Struct({
    canApprove: Schema.Boolean,
    canCancel: Schema.Boolean,
    canViewCosts: Schema.Boolean,
  }),
  cost: Schema.optionalKey(ResearchBillableCostProjectionSchema),
  approval: Schema.optionalKey(ResearchApprovalProjectionSchema),
  report: Schema.optionalKey(ResearchReportProjectionSchema),
  sources: Schema.Array(ResearchSourceProjectionSchema).check(Schema.isMaxLength(200)),
  milestones: Schema.Array(ResearchMilestoneProjectionSchema).check(
    Schema.isMaxLength(RESEARCH_RUN_DETAIL_MAX_MILESTONES),
  ),
}).check(
  Schema.makeFilter(({ run, report, permissions, cost }) => {
    if (permissions.canViewCosts !== (cost !== undefined)) {
      return "cost must be present exactly when canViewCosts is true";
    }
    const runIsReady = run.applicationStatus === "ready";
    const reportIsReady = report?.status === "ready";
    if (runIsReady !== reportIsReady) {
      return "ready run and report projections must be present together";
    }
    if (
      runIsReady &&
      (run.artifactRevisionId === undefined ||
        report?.status !== "ready" ||
        run.artifactRevisionId !== report.artifactRevisionId)
    ) {
      return "ready run and report projections must identify the same revision";
    }
    return (
      runIsReady ||
      run.artifactRevisionId === undefined ||
      "non-ready run projections must not identify a committed revision"
    );
  }),
);

export const ResearchPublicErrorCodeSchema = Schema.Literals([
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "NOT_FOUND",
  "INVALID_INPUT",
  "ARCHIVED",
  "RESEARCH_START_CONFLICT",
  "RESEARCH_PROVIDER_CONFLICT",
  "RESEARCH_SUBMISSION_CONFLICT",
  "RESEARCH_SUBMISSION_OUTCOME_UNKNOWN",
  "RESEARCH_NOT_READY",
  "RESEARCH_REVISION_CONFLICT",
  "RESEARCH_CONTENT_CORRUPT",
  "RESEARCH_LEASE_CONFLICT",
  "RESEARCH_FINALIZE_CONFLICT",
  "RESEARCH_TERMINAL",
  "RESEARCH_STORAGE_QUOTA_EXCEEDED",
  "RESEARCH_STORAGE_RATE_LIMITED",
  "STALE_ATTEMPT",
  "INSUFFICIENT_CREDITS",
  "RESEARCH_CREDIT_CONFLICT",
  "USAGE_CURRENCY_CONFLICT",
  "INTERNAL_ERROR",
]);
export const ResearchPublicErrorSchema = Schema.Struct({
  code: ResearchPublicErrorCodeSchema,
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500)),
});
export const ResearchRepositoryErrorDataSchema = Schema.Union([
  ResearchPublicErrorSchema,
  Schema.Struct({
    code: Schema.Literals(["QUOTA_EXCEEDED", "RATE_LIMITED", "UPLOAD_STATE_CONFLICT"]),
    message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500)),
  }),
]);

const ParallelWebhookMetadataValueSchema = Schema.Union([
  Schema.String,
  Schema.Finite,
  Schema.Boolean,
]);

/** Exact provider wire envelope accepted only after Standard Webhooks verification. */
export const ParallelTaskStatusWebhookSchema = Schema.Struct({
  timestamp: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(64)),
  type: Schema.Literal("task_run.status"),
  data: Schema.Struct({
    created_at: Schema.NullOr(Schema.String.check(Schema.isMaxLength(64))),
    interaction_id: ResearchIdentifierSchema,
    is_active: Schema.Boolean,
    modified_at: Schema.NullOr(Schema.String.check(Schema.isMaxLength(64))),
    processor: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
    run_id: ResearchIdentifierSchema,
    status: ParallelRunStateSchema,
    error: Schema.optionalKey(
      Schema.NullOr(
        Schema.Struct({
          message: Schema.String.check(Schema.isMaxLength(2_000)),
          ref_id: Schema.String.check(Schema.isMaxLength(180)),
          detail: Schema.optionalKey(Schema.NullOr(Schema.Unknown)),
        }),
      ),
    ),
    metadata: Schema.optionalKey(
      Schema.NullOr(Schema.Record(Schema.String, ParallelWebhookMetadataValueSchema)),
    ),
    taskgroup_id: Schema.optionalKey(Schema.NullOr(Schema.String.check(Schema.isMaxLength(180)))),
    warnings: Schema.optionalKey(
      Schema.NullOr(Schema.Array(Schema.Unknown).check(Schema.isMaxLength(64))),
    ),
  }),
});

export const decodePrepareResearchRunRequest = Schema.decodeUnknownSync(
  PrepareResearchRunRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodePrepareResearchRunResult = Schema.decodeUnknownSync(
  PrepareResearchRunResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeClaimResearchProviderSubmissionRequest = Schema.decodeUnknownSync(
  ClaimResearchProviderSubmissionRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeClaimResearchProviderSubmissionResult = Schema.decodeUnknownSync(
  ClaimResearchProviderSubmissionResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeBeginResearchProviderSubmissionRequest = Schema.decodeUnknownSync(
  BeginResearchProviderSubmissionRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeBeginResearchProviderSubmissionResult = Schema.decodeUnknownSync(
  BeginResearchProviderSubmissionResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeMarkResearchProviderSubmissionOutcomeRequest = Schema.decodeUnknownSync(
  MarkResearchProviderSubmissionOutcomeRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeMarkResearchProviderSubmissionOutcomeResult = Schema.decodeUnknownSync(
  MarkResearchProviderSubmissionOutcomeResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReleaseResearchProviderSubmissionClaimRequest = Schema.decodeUnknownSync(
  ReleaseResearchProviderSubmissionClaimRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReleaseResearchProviderSubmissionClaimResult = Schema.decodeUnknownSync(
  ReleaseResearchProviderSubmissionClaimResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeAttachResearchProviderRunRequest = Schema.decodeUnknownSync(
  AttachResearchProviderRunRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeAttachResearchProviderRunResult = Schema.decodeUnknownSync(
  AttachResearchProviderRunResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchProviderActivityCandidate = Schema.decodeUnknownSync(
  ResearchProviderActivityCandidateSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchProviderActivitySnapshot = Schema.decodeUnknownSync(
  ResearchProviderActivitySnapshotSchema,
  ResearchBoundaryParseOptions,
);
export const decodeClaimResearchProviderActivityObserverRequest = Schema.decodeUnknownSync(
  ClaimResearchProviderActivityObserverRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeClaimResearchProviderActivityObserverResult = Schema.decodeUnknownSync(
  ClaimResearchProviderActivityObserverResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeRenewResearchProviderActivityObserverRequest = Schema.decodeUnknownSync(
  RenewResearchProviderActivityObserverRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeRenewResearchProviderActivityObserverResult = Schema.decodeUnknownSync(
  RenewResearchProviderActivityObserverResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReleaseResearchProviderActivityObserverRequest = Schema.decodeUnknownSync(
  ReleaseResearchProviderActivityObserverRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReleaseResearchProviderActivityObserverResult = Schema.decodeUnknownSync(
  ReleaseResearchProviderActivityObserverResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReplaceResearchProviderActivityRequest = Schema.decodeUnknownSync(
  ReplaceResearchProviderActivityRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReplaceResearchProviderActivityResult = Schema.decodeUnknownSync(
  ReplaceResearchProviderActivityResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeObserveResearchProviderTerminalRequest = Schema.decodeUnknownSync(
  ObserveResearchProviderTerminalRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeObserveResearchProviderTerminalResult = Schema.decodeUnknownSync(
  ObserveResearchProviderTerminalResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeClaimResearchFinalizationRequest = Schema.decodeUnknownSync(
  ClaimResearchFinalizationRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeClaimResearchFinalizationResult = Schema.decodeUnknownSync(
  ClaimResearchFinalizationResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReleaseResearchFinalizationLeaseRequest = Schema.decodeUnknownSync(
  ReleaseResearchFinalizationLeaseRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReleaseResearchFinalizationLeaseResult = Schema.decodeUnknownSync(
  ReleaseResearchFinalizationLeaseResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReserveResearchFinalizationObjectsRequest = Schema.decodeUnknownSync(
  ReserveResearchFinalizationObjectsRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReserveResearchFinalizationObjectsResult = Schema.decodeUnknownSync(
  ReserveResearchFinalizationObjectsResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReconcileResearchCostsRequest = Schema.decodeUnknownSync(
  ReconcileResearchCostsRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeReconcileResearchCostsResult = Schema.decodeUnknownSync(
  ReconcileResearchCostsResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeGetResearchRecoveryContextRequest = Schema.decodeUnknownSync(
  GetResearchRecoveryContextRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeGetResearchRecoveryContextResult = Schema.decodeUnknownSync(
  GetResearchRecoveryContextResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeListRecoverableResearchRunsRequest = Schema.decodeUnknownSync(
  ListRecoverableResearchRunsRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeListRecoverableResearchRunsResult = Schema.decodeUnknownSync(
  ListRecoverableResearchRunsResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeAuthorizeResearchReportReadRequest = Schema.decodeUnknownSync(
  AuthorizeResearchReportReadRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeAuthorizeResearchReportReadResult = Schema.decodeUnknownSync(
  AuthorizeResearchReportReadResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeCommitResearchFinalizationRequest = Schema.decodeUnknownSync(
  CommitResearchFinalizationRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeCommitResearchFinalizationResult = Schema.decodeUnknownSync(
  CommitResearchFinalizationResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeFailResearchFinalizationRequest = Schema.decodeUnknownSync(
  FailResearchFinalizationRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeFailResearchFinalizationResult = Schema.decodeUnknownSync(
  FailResearchFinalizationResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeClaimResearchStorageCleanupRequest = Schema.decodeUnknownSync(
  ClaimResearchStorageCleanupRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeClaimResearchStorageCleanupResult = Schema.decodeUnknownSync(
  ClaimResearchStorageCleanupResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeRecordResearchStorageCleanupRequest = Schema.decodeUnknownSync(
  RecordResearchStorageCleanupRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeRecordResearchStorageCleanupResult = Schema.decodeUnknownSync(
  RecordResearchStorageCleanupResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeCancelResearchRunRequest = Schema.decodeUnknownSync(
  CancelResearchRunRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeCancelResearchRunResult = Schema.decodeUnknownSync(
  CancelResearchRunResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeParallelTaskStatusWebhook = Schema.decodeUnknownSync(
  ParallelTaskStatusWebhookSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchRunProjection = Schema.decodeUnknownSync(
  ResearchRunProjectionSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchMilestoneProjection = Schema.decodeUnknownSync(
  ResearchMilestoneProjectionSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchRunDetail = Schema.decodeUnknownSync(
  ResearchRunDetailSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchPublicError = Schema.decodeUnknownSync(
  ResearchPublicErrorSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchRepositoryErrorData = Schema.decodeUnknownSync(
  ResearchRepositoryErrorDataSchema,
  ResearchBoundaryParseOptions,
);
export const decodeAuthorizedResearchReportReadRequest = Schema.decodeUnknownSync(
  AuthorizedResearchReportReadRequestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeAuthorizedResearchReportContent = Schema.decodeUnknownSync(
  AuthorizedResearchReportContentSchema,
  ResearchBoundaryParseOptions,
);
export const decodeAuthorizedResearchReportReadResult = Schema.decodeUnknownSync(
  AuthorizedResearchReportReadResultSchema,
  ResearchBoundaryParseOptions,
);

export type ResearchProductMode = typeof ResearchProductModeSchema.Type;
export type PrepareResearchRunRequest = typeof PrepareResearchRunRequestSchema.Type;
export type PrepareResearchRunResult = typeof PrepareResearchRunResultSchema.Type;
export type ClaimResearchProviderSubmissionRequest =
  typeof ClaimResearchProviderSubmissionRequestSchema.Type;
export type ClaimResearchProviderSubmissionResult =
  typeof ClaimResearchProviderSubmissionResultSchema.Type;
export type BeginResearchProviderSubmissionRequest =
  typeof BeginResearchProviderSubmissionRequestSchema.Type;
export type BeginResearchProviderSubmissionResult =
  typeof BeginResearchProviderSubmissionResultSchema.Type;
export type MarkResearchProviderSubmissionOutcomeRequest =
  typeof MarkResearchProviderSubmissionOutcomeRequestSchema.Type;
export type MarkResearchProviderSubmissionOutcomeResult =
  typeof MarkResearchProviderSubmissionOutcomeResultSchema.Type;
export type ReleaseResearchProviderSubmissionClaimRequest =
  typeof ReleaseResearchProviderSubmissionClaimRequestSchema.Type;
export type ReleaseResearchProviderSubmissionClaimResult =
  typeof ReleaseResearchProviderSubmissionClaimResultSchema.Type;
export type AttachResearchProviderRunRequest = typeof AttachResearchProviderRunRequestSchema.Type;
export type AttachResearchProviderRunResult = typeof AttachResearchProviderRunResultSchema.Type;
export type ResearchProviderActivityPhase = typeof ResearchProviderActivityPhaseSchema.Type;
export type ResearchProviderActivityCandidate = typeof ResearchProviderActivityCandidateSchema.Type;
export type ResearchProviderActivityCandidateEncoded =
  typeof ResearchProviderActivityCandidateSchema.Encoded;
export type ResearchProviderActivitySnapshot = typeof ResearchProviderActivitySnapshotSchema.Type;
export type ResearchProviderActivitySnapshotEncoded =
  typeof ResearchProviderActivitySnapshotSchema.Encoded;
export type ClaimResearchProviderActivityObserverRequest =
  typeof ClaimResearchProviderActivityObserverRequestSchema.Type;
export type ClaimResearchProviderActivityObserverRequestEncoded =
  typeof ClaimResearchProviderActivityObserverRequestSchema.Encoded;
export type ClaimResearchProviderActivityObserverResult =
  typeof ClaimResearchProviderActivityObserverResultSchema.Type;
export type ClaimResearchProviderActivityObserverResultEncoded =
  typeof ClaimResearchProviderActivityObserverResultSchema.Encoded;
export type RenewResearchProviderActivityObserverRequest =
  typeof RenewResearchProviderActivityObserverRequestSchema.Type;
export type RenewResearchProviderActivityObserverRequestEncoded =
  typeof RenewResearchProviderActivityObserverRequestSchema.Encoded;
export type RenewResearchProviderActivityObserverResult =
  typeof RenewResearchProviderActivityObserverResultSchema.Type;
export type RenewResearchProviderActivityObserverResultEncoded =
  typeof RenewResearchProviderActivityObserverResultSchema.Encoded;
export type ReleaseResearchProviderActivityObserverRequest =
  typeof ReleaseResearchProviderActivityObserverRequestSchema.Type;
export type ReleaseResearchProviderActivityObserverRequestEncoded =
  typeof ReleaseResearchProviderActivityObserverRequestSchema.Encoded;
export type ReleaseResearchProviderActivityObserverResult =
  typeof ReleaseResearchProviderActivityObserverResultSchema.Type;
export type ReleaseResearchProviderActivityObserverResultEncoded =
  typeof ReleaseResearchProviderActivityObserverResultSchema.Encoded;
export type ReplaceResearchProviderActivityRequest =
  typeof ReplaceResearchProviderActivityRequestSchema.Type;
export type ReplaceResearchProviderActivityRequestEncoded =
  typeof ReplaceResearchProviderActivityRequestSchema.Encoded;
export type ReplaceResearchProviderActivityResult =
  typeof ReplaceResearchProviderActivityResultSchema.Type;
export type ReplaceResearchProviderActivityResultEncoded =
  typeof ReplaceResearchProviderActivityResultSchema.Encoded;
export type ObserveResearchProviderTerminalRequest =
  typeof ObserveResearchProviderTerminalRequestSchema.Type;
export type ObserveResearchProviderTerminalResult =
  typeof ObserveResearchProviderTerminalResultSchema.Type;
export type ClaimResearchFinalizationRequest = typeof ClaimResearchFinalizationRequestSchema.Type;
export type ClaimResearchFinalizationResult = typeof ClaimResearchFinalizationResultSchema.Type;
export type ReleaseResearchFinalizationLeaseRequest =
  typeof ReleaseResearchFinalizationLeaseRequestSchema.Type;
export type ReleaseResearchFinalizationLeaseResult =
  typeof ReleaseResearchFinalizationLeaseResultSchema.Type;
export type ReserveResearchFinalizationObjectsRequest =
  typeof ReserveResearchFinalizationObjectsRequestSchema.Type;
export type ReserveResearchFinalizationObjectsResult =
  typeof ReserveResearchFinalizationObjectsResultSchema.Type;
export type ReconcileResearchCostsRequest = typeof ReconcileResearchCostsRequestSchema.Type;
export type ReconcileResearchCostsResult = typeof ReconcileResearchCostsResultSchema.Type;
export type GetResearchRecoveryContextRequest = typeof GetResearchRecoveryContextRequestSchema.Type;
export type GetResearchRecoveryContextResult = typeof GetResearchRecoveryContextResultSchema.Type;
export type ListRecoverableResearchRunsRequest =
  typeof ListRecoverableResearchRunsRequestSchema.Type;
export type ListRecoverableResearchRunsResult = typeof ListRecoverableResearchRunsResultSchema.Type;
export type AuthorizeResearchReportReadRequest =
  typeof AuthorizeResearchReportReadRequestSchema.Type;
export type AuthorizeResearchReportReadResult = typeof AuthorizeResearchReportReadResultSchema.Type;
export type ResearchStorageObjectKind = typeof ResearchStorageObjectKindSchema.Type;
export type ResearchStorageObjectCommit = typeof ResearchStorageObjectCommitSchema.Type;
export type CommitResearchFinalizationRequest = typeof CommitResearchFinalizationRequestSchema.Type;
export type CommitResearchFinalizationResult = typeof CommitResearchFinalizationResultSchema.Type;
export type FailResearchFinalizationRequest = typeof FailResearchFinalizationRequestSchema.Type;
export type FailResearchFinalizationResult = typeof FailResearchFinalizationResultSchema.Type;
export type ClaimResearchStorageCleanupRequest =
  typeof ClaimResearchStorageCleanupRequestSchema.Type;
export type ResearchStorageCleanupClaim = typeof ResearchStorageCleanupClaimSchema.Type;
export type ClaimResearchStorageCleanupResult = typeof ClaimResearchStorageCleanupResultSchema.Type;
export type RecordResearchStorageCleanupRequest =
  typeof RecordResearchStorageCleanupRequestSchema.Type;
export type RecordResearchStorageCleanupResult =
  typeof RecordResearchStorageCleanupResultSchema.Type;
export type CancelResearchRunRequest = typeof CancelResearchRunRequestSchema.Type;
export type CancelResearchRunResult = typeof CancelResearchRunResultSchema.Type;
export type ResearchRunProjection = typeof ResearchRunProjectionSchema.Type;
export type ResearchBillableCostProjection = typeof ResearchBillableCostProjectionSchema.Type;
export type ResearchSourceProjection = typeof ResearchSourceProjectionSchema.Type;
export type ResearchReportProjection = typeof ResearchReportProjectionSchema.Type;
export type ResearchMilestoneProjection = typeof ResearchMilestoneProjectionSchema.Type;
export type ResearchRunDetail = typeof ResearchRunDetailSchema.Type;
export type ParallelTaskStatusWebhook = typeof ParallelTaskStatusWebhookSchema.Type;
export type ResearchPublicError = typeof ResearchPublicErrorSchema.Type;
